import { useCallback, useMemo, useRef, useState } from "react";
import { IItemsListDeletedFilter } from "../types/ItemsList.types";
import {
  applyDeletedFilter,
  loadShowDeletedFromStorage,
  saveShowDeletedToStorage,
} from "../utils/itemsListFilterUtils";

export const useItemsListFilters = <TItem extends { [key: string]: any }>(
  items: TItem[],
  deletedFilter?: IItemsListDeletedFilter<TItem>,
) => {
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(() =>
    deletedFilter
      ? loadShowDeletedFromStorage(deletedFilter.storageKey)
      : false,
  );

  // Pages pass the filter as an inline object, so the predicate is a brand new
  // function on every render. Keeping it in a ref keeps the memo below stable -
  // otherwise the filtered array would change identity on every render and
  // retrigger the search effect in useItemsList endlessly.
  const isDeletedRef = useRef(deletedFilter?.isDeleted);
  isDeletedRef.current = deletedFilter?.isDeleted;

  const storageKey = deletedFilter?.storageKey;
  // Stable dependency for the memos below: the predicate itself cannot be one,
  // but whether the list has a filter at all can change (ItemsList drops it for
  // headerless lists) and both memos must follow that.
  const hasDeletedFilter = !!deletedFilter?.isDeleted;

  const openFiltersPanel = useCallback(() => setIsFiltersPanelOpen(true), []);
  const closeFiltersPanel = useCallback(() => setIsFiltersPanelOpen(false), []);

  const updateShowDeleted = useCallback(
    (value: boolean) => {
      setShowDeleted(value);

      if (storageKey) {
        saveShowDeletedToStorage(storageKey, value);
      }
    },
    [storageKey],
  );

  const clearAllFilters = useCallback(
    () => updateShowDeleted(false),
    [updateShowDeleted],
  );

  const visibleItems = useMemo(
    () => applyDeletedFilter(items, isDeletedRef.current, showDeleted),
    [items, showDeleted, hasDeletedFilter],
  );

  // Always counts the non-deleted items, no matter what the toggle says, so
  // the number next to a page title means "how many live records are there".
  const activeItemsCount = useMemo(
    () => applyDeletedFilter(items, isDeletedRef.current, false).length,
    [items, hasDeletedFilter],
  );

  const activeFiltersCount = showDeleted ? 1 : 0;

  return {
    activeItemsCount,
    isFiltersPanelOpen,
    openFiltersPanel,
    closeFiltersPanel,
    showDeleted,
    updateShowDeleted,
    clearAllFilters,
    visibleItems,
    activeFiltersCount,
  };
};
